import {
  Box,
  Button,
  Fab,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ConfirmationNumber } from '@mui/icons-material';
import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { FormInputDropzone } from './FormInputDropzone';
import { useAppDispatch } from 'store';
import message from 'plugins/utils/message';
import { createTicket } from 'store/slices/ticketSlice';
import { obtenerParametricas } from 'repositories/parametrica_repository';
import { Autocomplete } from '@mui/material';

export interface ITicketForm {
  asunto: string;
  descripcion: string;
  location: string;
  files: string;
}

type ParametricaOption = {
  id: string;
  codigo: string;
  grupo: string;
  nombre: string;
  descripcion: string;
};

export type SelectOption = {
  value: string | number;
  label: string;
  caption: string;
};

export const POSITION_DEFAULT = { x: 170, y: 80 };

const ButtonCreateTicket = ({
  user,
}: {
  user: {
    email: string;
    rol: string;
  };
}) => {
  const { handleSubmit, control, trigger, reset, setValue } =
    useForm<ITicketForm>({
      shouldUnregister: false,
      mode: 'all',
    });

  const [isFormOpen, setIsFormOpen] = useState(false);

  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const handleFabClick = () => {
    if (!isFormOpen) {
      const defaultX = window.innerWidth - POSITION_DEFAULT.x;
      const defaultY = window.innerHeight - POSITION_DEFAULT.y;
      setPosition({ x: defaultX, y: defaultY });
    }

    setIsFormOpen((prev) => !prev);
  };

  const [parametricaTipoProblema, setParametricaTipoProblema] = React.useState<
    SelectOption[]
  >([]);

  const [parametricasPorProblema, setParametricasPorProblema] = React.useState<
    SelectOption[]
  >([]);

  const onSubmit = (data: ITicketForm) => {
    void trigger();

    if (data.asunto.trim().length === 0) {
      message.error('El asunto es requerido');
      return;
    }

    if (data.descripcion.trim().length === 0) {
      message.error('La descripción es requerida');
      return;
    }
    void dispatch(createTicket(data));

    message.success(t('form.creandoTicket'));

    setIsFormOpen(false);
    reset();
  };

  const [position, setPosition] = useState({
    x: window.innerWidth - POSITION_DEFAULT.x,
    y: window.innerHeight - POSITION_DEFAULT.y,
  });

  const handleDrag = (e: React.DragEvent<HTMLDivElement>) => {
    if (isFormOpen) return;
    const target = e.target as HTMLDivElement;
    const newX = e.clientX - target.offsetWidth / 2;
    const newY = e.clientY - target.offsetHeight / 2;
    setPosition({ x: newX, y: newY });
  };

  useEffect(() => {
    const defaultX = window.innerWidth - POSITION_DEFAULT.x;
    const defaultY = window.innerHeight - POSITION_DEFAULT.y;
    setPosition({ x: defaultX, y: defaultY });
  }, []);

  const cargarOpcionesPorProblema = async (tipoProblema: string) => {
    if (tipoProblema.trim().length === 0) return;
    setValue('descripcion', '');
    const listado = (await obtenerParametricas(tipoProblema)) as ParametricaOption[];
    const problemasTickets: SelectOption[] = (listado || []).map((item) => ({
      value: item.nombre || '',
      label: `${item.nombre}`,
      caption: item.codigo,
    }));
    setParametricasPorProblema(problemasTickets);
  };

  const cargarOpciones = async () => {
    const parametricasResponse = (await obtenerParametricas(
      'TIPO_PROBLEMA_TICKET',
    )) as ParametricaOption[];
    const listado = parametricasResponse.sort((a, b) =>
      a.id.localeCompare(b.id),
    );
    const problemasTickets: SelectOption[] = (listado || []).map((item) => ({
      value: item.nombre || '',
      label: `${item.nombre}`,
      caption: item.codigo,
    }));
    setParametricaTipoProblema(problemasTickets);
  };
  const cargarOpcionesCallback = React.useCallback(cargarOpciones, []);

  React.useEffect(() => {
    if (isFormOpen) void cargarOpcionesCallback();
  }, [cargarOpcionesCallback, isFormOpen]);

  useEffect(() => {
    setIsFormOpen(false);
    reset();
    setParametricasPorProblema([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem('locale')]);

  return (
    <div
      style={{
        position: 'fixed',
        left: position.x,
        top: position.y,
        cursor: 'move',
        zIndex: 50,
      }}
      draggable
      onDragEnd={handleDrag}
    >
      {(user.rol === 'PROFESOR' || user.rol === 'MONITOR') && (
        <>
          <Fab
            variant="extended"
            sx={{ gap: '10px', display: 'flex', justifyContent: 'center', whiteSpace: 'nowrap' }}
            size="small"
            color="primary"
            onClick={handleFabClick}
          >
            <ConfirmationNumber />
            <span
              style={{
                textAlign: 'center',
                textTransform: 'none',
                whiteSpace: 'nowrap',
              }}
            >
              {t('crearTicket')}
            </span>
          </Fab>

          {isFormOpen && (
            <Box component="form" sx={{ overflowY: 'auto', overflowX: 'hidden', position: 'fixed', bottom: '100px', right: '20px', zIndex: 10, backgroundColor: 'white', p: '20px', boxShadow: '0 4px 8px rgba(0,0,0,0.1)', borderRadius: '8px', border: '1px solid rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', maxWidth: '400px', maxHeight: 'calc(100vh - 150px)', gap: '10px' }}>
              <Box style={{ gap: '10px' }}>
                <Typography
                  variant="body1"
                  style={{ fontWeight: 'bold', textAlign: 'center' }}
                >
                  {t('crearTicket')}
                </Typography>

                <Typography
                  variant="body2"
                  style={{
                    marginTop: '10px',
                    textAlign: 'justify',
                  }}
                >
                  {t('infoCrearTicket')}
                </Typography>
              </Box>

              <Grid
                container
                style={{
                  marginTop: '10px',
                }}
                spacing={2}
              >
                <Grid size={12}>
                  <Controller
                    name="asunto"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <FormControl
                        variant="filled"
                        required
                        sx={{ width: '100%' }}
                      >
                        <InputLabel>{t('form.motivoTicket')}</InputLabel>
                        <Select
                          {...field}
                          onChange={(e) => {
                            const valorSeleccionado = e.target.value;

                            const parametrica = parametricaTipoProblema.find(
                              (option) => option.value === valorSeleccionado,
                            );

                            if (parametrica) {
                              field.onChange(e);
                              void cargarOpcionesPorProblema(
                                parametrica.caption || '',
                              );
                            } else {
                              console.error(
                                'El valor seleccionado no es válido',
                              );
                            }
                          }}
                        >
                          {parametricaTipoProblema.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="descripcion"
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <FormControl
                        variant="filled"
                        required
                        disabled={parametricasPorProblema.length === 0}
                        sx={{ width: '100%' }}
                      >
                        <Autocomplete
                          options={parametricasPorProblema}
                          autoHighlight
                          getOptionLabel={(option: SelectOption) =>
                            option.label
                          }
                          renderOption={({ key, ...props }, option: SelectOption) => (
                            <li key={key} {...props}>
                              <Box>
                                <Typography variant="body2">
                                  {option.label}
                                </Typography>
                              </Box>
                            </li>
                          )}
                          value={
                            parametricasPorProblema.find(
                              (option) => option.value === field.value,
                            ) || null
                          }
                          onChange={(_event, newValue: SelectOption | null) => {
                            field.onChange(newValue ? newValue.value : '');
                          }}
                          disabled={parametricasPorProblema.length === 0}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={t('form.descripcionTicket')}
                              variant="filled"
                              slotProps={{
                                ...params.slotProps,
                                htmlInput: {
                                  ...params.slotProps?.htmlInput,
                                  autoComplete: 'new-password',
                                },
                              }}
                            />
                          )}
                        />
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="location"
                    control={control}
                    defaultValue=""
                    rules={{
                      pattern: {
                        value: /^[^|]*$/,
                        message: 'Cannot contain |',
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="filled"
                        label={t('descripcionUbicacion')}
                        required
                        placeholder={t('form.ubicacionTicket')}
                        error={!!fieldState.error}
                      />
                    )}
                  />
                </Grid>

                <Grid size={12}>
                  <Controller
                    name="files"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FormInputDropzone
                        label={t('form.subirArchivos')}
                        name={field.name}
                        value={field.value}
                        onChange={field.onChange}
                        acceptedFiles={['image/*', 'application/pdf']}
                        error={!!fieldState.error}
                        maxFileSize={10 * 1024 * 1024}
                        filesLimit={3}
                        dropzoneText={t('form.dropzoneTextFiles')}
                        helperText={
                          fieldState.error
                            ? 'Please upload valid files'
                            : undefined
                        }
                      />
                    )}
                  />
                </Grid>

                <Button
                  style={{
                    marginLeft: 'auto',
                    marginTop: '10px',
                  }}
                  variant="contained"
                  color="primary"
                  onClick={handleSubmit(onSubmit)}
                >
                  {t('enviarTicket')}
                </Button>
              </Grid>
            </Box>
          )}
        </>
      )}
    </div>
  );
};

export default ButtonCreateTicket;
